// The new unified HackerLabs entry point. One screen, one bar, one
// orchestration layer. Replaces the old `/` (Index) experience.
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import FunctionStrip from "@/components/ai-home/FunctionStrip";
import MemoryPopover from "@/components/ai-home/MemoryPopover";
import LibrarySheet from "@/components/ai-home/LibrarySheet";
import HackathonPotCard from "@/components/ai-home/HackathonPotCard";
import WalletChip from "@/components/ai-home/WalletChip";
import UnifiedMenu from "@/components/ai-home/UnifiedMenu";
import MultiChat from "@/components/ai-home/MultiChat";

export default function HomeAI() {
  // Library still seeds prompts via window event.
  const seedPrompt = (p: string) => {
    window.dispatchEvent(new CustomEvent("multichat-seed", { detail: p }));
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      <Helmet>
        <title>hackerlabs.ai — one bar, every AI, get it done</title>
        <meta name="description" content="The unified AI operating layer. One bar routes your prompt to the cheapest capable model, lets you swap to a stronger one, and shares what you make with the community." />
        <link rel="canonical" href="https://hackerlabs.ai/" />
      </Helmet>

      {/* Ambient background glow */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary/10 blur-[140px]" />
      </div>

      {/* One menu, one place — works on mobile + desktop, pops everything out. */}
      <UnifiedMenu />

      {/* Wallet stays visible top-right so users always see their balance.
          Memory + Library are mounted (no trigger) so the unified menu can
          pop them open via window events. */}
      <header className="fixed top-3 right-3 z-30 flex items-center gap-2">
        <WalletChip />
      </header>
      <MemoryPopover hideTrigger />
      <LibrarySheet hideTrigger onRun={(p) => seedPrompt(p)} />

      <main className="container mx-auto px-4 pt-24 sm:pt-28 pb-24 max-w-3xl">
        <HackathonPotCard />
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" aria-label="hackerlabs.ai home">
            <span className="text-3xl sm:text-5xl font-mono font-extrabold tracking-tight lowercase">
              <span className="text-foreground">hacker</span>
              <span className="text-primary">labs</span>
              <span className="text-muted-foreground">.ai</span>
            </span>
          </Link>
        </div>

        <MultiChat />
        <FunctionStrip />
      </main>
    </div>
  );
}
